import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MessageCircle, X, Send, Sparkles, GripVertical, Loader2 } from "lucide-react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function FloatingBot({ context = null }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Position: bottom-left by default (x from left, y from bottom)
  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return { x: 24, y: 24 };
    try {
      const raw = localStorage.getItem("lumi-pos");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { x: 24, y: 24 };
  });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const listRef = useRef(null);

  useEffect(() => { localStorage.setItem("lumi-pos", JSON.stringify(pos)); }, [pos]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, ox: pos.x, oy: pos.y };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp, { once: true });
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    // x measured from left, y from bottom
    const nextX = clamp(dragStart.current.ox + dx, 8, window.innerWidth - 80);
    const nextY = clamp(dragStart.current.oy - dy, 8, window.innerHeight - 80);
    setPos({ x: nextX, y: nextY });
  };
  const onMouseUp = () => {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
  };

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    const next = [...messages, { role: "user", text: q }];
    setMessages(next);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", {
        message: q,
        session_id: `bot_ui_${lang}`,
        model: "gemini",
        context: context || undefined,
      });
      setMessages((m) => [...m, { role: "bot", text: data.reply || "" }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: lang === "vi" ? "Có lỗi xảy ra. Vui lòng thử lại." : "Something went wrong. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const style = { left: pos.x, bottom: pos.y };

  return (
    <div className="fixed z-50" style={style} data-testid="floating-bot">
      {open && (
        <div className="mb-3 w-[340px] sm:w-[380px] max-h-[520px] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/60 backdrop-blur">
            <div
              className="flex items-center gap-2 cursor-grab active:cursor-grabbing select-none"
              onMouseDown={onMouseDown}
              data-testid="bot-drag-handle"
              title="Drag me"
            >
              <GripVertical size={14} className="text-muted-foreground" />
              <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center">
                <Sparkles size={15} />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">{t("bot.title")}</p>
                <p className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">{t("bot.subtitle")}</p>
              </div>
            </div>
            <button
              data-testid="bot-close-btn"
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-accent grid place-items-center"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>
          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="bot-empty">
                {t("bot.empty")}
              </p>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-muted px-3.5 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" />
                  <span className="text-muted-foreground">Lumi…</span>
                </div>
              </div>
            )}
          </div>
          {/* Input */}
          <div className="border-t border-border p-3">
            <div className="flex items-center gap-2">
              <input
                data-testid="bot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder={t("bot.placeholder")}
                className="flex-1 h-10 rounded-full border border-border bg-background px-4 text-sm focus:outline-none focus:border-primary"
              />
              <button
                data-testid="bot-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center disabled:opacity-50 hover:brightness-110 transition"
                aria-label={t("bot.send")}
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bubble */}
      <button
        data-testid="bot-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        onMouseDown={(e) => { if (e.shiftKey) onMouseDown(e); }}
        className="relative w-14 h-14 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-xl hover:scale-105 transition-transform pulse-primary"
        title="Chat with Lumi (Shift-drag to move)"
        aria-label="Open Lumi chat"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
    </div>
  );
}
